'use client';

import * as React from 'react';
import styles from './Breadcrumbs.module.css';
import clsx from 'clsx';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  maxItems?: number;
  className?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  separator,
  maxItems,
  className,
}) => {
  const defaultSeparator = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.separatorIcon}>
      <path d="M9 18l6-6-6-6" />
    </svg>
  );

  let displayedItems = items;
  let showEllipsis = false;

  if (maxItems && items.length > maxItems) {
    const firstItem = items[0];
    const lastItems = items.slice(-(maxItems - 1));
    displayedItems = [firstItem, ...lastItems];
    showEllipsis = true;
  }

  return (
    <nav aria-label="Breadcrumb" className={clsx(styles.breadcrumbs, className)}>
      <ol className={styles.list}>
        {displayedItems.map((item, index) => {
          const isLast = index === displayedItems.length - 1;
          const showEllipsisHere = showEllipsis && index === 0;

          return (
            <React.Fragment key={index}>
              <li className={styles.item}>
                {item.href && !isLast ? (
                  <a href={item.href} className={styles.link}>
                    {item.icon && <span className={styles.icon}>{item.icon}</span>}
                    {item.label}
                  </a>
                ) : (
                  <span className={clsx(styles.text, isLast && styles.current)} aria-current={isLast ? 'page' : undefined}>
                    {item.icon && <span className={styles.icon}>{item.icon}</span>}
                    {item.label}
                  </span>
                )}
              </li>
              {showEllipsisHere && (
                <>
                  <li className={styles.separator} aria-hidden="true">
                    {separator || defaultSeparator}
                  </li>
                  <li className={styles.item}>
                    <span className={styles.ellipsis}>...</span>
                  </li>
                </>
              )}
              {!isLast && (
                <li className={styles.separator} aria-hidden="true">
                  {separator || defaultSeparator}
                </li>
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};
